import {Box, Button, IconButton, Stack, TextField, Typography} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Tooltip from "@mui/material/Tooltip";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import {DataGrid, GridColDef} from "@mui/x-data-grid";
import ConfirmationModal from "../../components/ConfirmationModal";
import React, {useEffect, useState} from "react";
import {createToastNotification} from "../../utilities/helpers";
import {IFetchFloorPlanBaseDto, IFloorPlanBase} from "../../common/interfaces/floor-plan-base";
import {deleteFloorPlanBase, fetchFloorPlansBase} from "../../api/floor-plan-base";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {FloorPlanBaseModal} from "./modal/FloorPlanBaseModal";

export const FloorPlansBase = () => {

    const [search, setSearch] = useState<string>('');
    const [page, setPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(10);
    const paginationModel = {page: page, pageSize: perPage};


    const [selectedBase, setSelectedBase] = useState<IFloorPlanBase | null >(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [confirmDeleteDialogIsOpened, setConfirmDeleteDialogIsOpened] = useState(false);

    const columns: GridColDef[] = [
        {field: 'id', headerName: 'ID', width: 70,sortable: false},
        {field: 'name', headerName: 'Naziv', flex: 2,sortable: false},
        {field: 'description', headerName: 'Opis', type: 'string', flex: 3,sortable: false},
        {
            field: 'actions',
            headerName: '',
            width: 100,
            sortable: false,
            flex: 1,
            minWidth: 100,
            renderCell: (params) => (
                <IconButton
                    color="error"
                    onClick={(event) => {
                        event.stopPropagation();
                        handleOpenDeleteDialog(params.row);
                    }}
                >
                    <DeleteOutlineIcon color="error" />
                </IconButton>
            ),
        },
    ];
    const [tableData, setTableData] = useState<IFloorPlanBase[]>([]);
    const [totalBases, setTotalBases] = useState<number>(0);


    const handleFetchFloorPlanBases = async (searchParam?: string) => {

        const data: IFetchFloorPlanBaseDto = {
            page: page,
            perPage: perPage,
        };

        if (searchParam !== undefined) {
            // @ts-ignore
            data.search = searchParam
        } else if (search.length > 0) {
            // @ts-ignore
            data.search = search
        }

        try {
            let response = await fetchFloorPlansBase(data);
            setTableData(response.bases)
            setTotalBases(response.totalItems)
        } catch (error: any) {
            createToastNotification('error', error?.response?.data?.message);
        }
    }

    // @ts-ignore
    const handleRowClick = async (params) => {
        try {
            setModalOpen(true);
            setSelectedBase(params.row)
        } catch (error: any) {
            createToastNotification('error', error?.response?.data?.message);
        }
    }

    // @ts-ignore
    const handleOpenDeleteDialog = (base: IFloorPlanBase) => {
        setSelectedBase(base);
        setConfirmDeleteDialogIsOpened(true);
    }

    const handleCloseDeleteDialog = () => {
        setSearch('')
        setConfirmDeleteDialogIsOpened(false);
    }

    // @ts-ignore
    const handleConfirmDelete = async () => {
        if(!selectedBase?.id) {
            setSelectedBase(null)
            setConfirmDeleteDialogIsOpened(false);
            return;
        }
        try {
            await deleteFloorPlanBase(selectedBase.id);
            createToastNotification('success', 'Osnova prostornog plana uspešno izbrisana');
        } catch (error: any) {
            createToastNotification('error', error?.response?.data?.message);
        } finally {
            setSelectedBase(null)
            setConfirmDeleteDialogIsOpened(false);
            await handleFetchFloorPlanBases();
        }
    }

    useEffect(() => {
        handleFetchFloorPlanBases()
    }, [page,perPage]);

    return (
        <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h4">Lista osnova prostorih planova</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        setModalOpen(true);
                        setSelectedBase(null);
                    }}
                    startIcon={<AddIcon />}
                >
                    Dodaj novu osnovu prostornog plana
                </Button>
            </Box>
            <br />
            <Box width="100%">
                <Stack direction="row" alignItems="center">
                    <TextField
                        sx={{
                            width: '500px',
                            height: '50px',
                            marginRight: '20px',
                            '& .MuiInputBase-root': {
                                backgroundColor: 'white',
                                borderRadius: '5px',
                                padding: 0,
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#d0d0d0',
                            },
                        }}
                        label="Pretraga po nazivu"
                        name="search"
                        size="small"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleFetchFloorPlanBases(search);
                            }
                        }}
                        margin="normal"
                    />
                    <Tooltip title="Pretraži">
                        <IconButton
                            onClick={()=>{
                                handleFetchFloorPlanBases()
                            }}
                            color="primary"
                            size="medium"
                            sx={{
                                backgroundColor: '#f0f0f0',
                                '&:hover': {
                                    backgroundColor: '#e0e0e0',
                                },
                            }}
                        >
                            <SearchIcon />
                        </IconButton>
                    </Tooltip>
                    {search.length>0 &&
                        <Tooltip title="Očisti unos">
                            <IconButton
                                onClick={()=>{
                                    setSearch('')
                                    handleFetchFloorPlanBases('')
                                }}
                                color="error"
                                size="medium"
                                sx={{
                                    marginLeft:1,
                                    backgroundColor: '#f0f0f0',
                                    '&:hover': {
                                        backgroundColor: '#e0e0e0',
                                    },
                                }}
                            >
                                <ClearIcon />
                            </IconButton>
                        </Tooltip>}
                </Stack>
                <DataGrid
                    disableColumnMenu
                    disableRowSelectionOnClick
                    disableMultipleRowSelection
                    rows={tableData}
                    rowCount={totalBases}
                    columns={columns}
                    onRowClick={handleRowClick}
                    pagination
                    paginationModel={{
                        page: paginationModel.page - 1,
                        pageSize: paginationModel.pageSize
                    }}
                    paginationMode={'server'}
                    pageSizeOptions={[5, 10]}
                    //@ts-ignore
                    onPaginationModelChange={(model) => {
                        setPage(model.page + 1)
                        setPerPage(model.pageSize)
                    }}
                    sx={{
                        marginTop:2,
                        paddingLeft:'10px',
                        paddingRight:'10px',
                        border: 0,
                        '.MuiDataGrid-cell:focus': {
                            outline: 'none',
                        }
                    }}
                    localeText={{
                        MuiTablePagination: {
                            labelRowsPerPage: "Redova po strani",
                            labelDisplayedRows: ({from, to, count}) =>
                                `${from} - ${to} od ${count !== -1 ? count : ` ${to}`}`,
                        },
                        noRowsLabel: "Nema dostupnih podataka",
                    }}
                />
                <FloorPlanBaseModal
                    open={modalOpen}
                    onClose={()=> {
                        setModalOpen(false)
                        handleFetchFloorPlanBases()
                    }}
                    base={selectedBase}
                />
                {confirmDeleteDialogIsOpened && (
                    <ConfirmationModal
                        confirmationModalTitle="Brisanje osnove prostornog plana"
                        cancelAction={() => {
                            handleCloseDeleteDialog()
                        }}
                        handleAction={()=> {
                            handleConfirmDelete()
                        }}
                        confirmBtnTitle="Potvrdi brisanje"
                        modalDescription="Da li ste sigurni da želite da obrišete osnovu prostornog plana?"
                        cancelBtnTitle="Odustani"
                        confirmButtonColor={'red'}
                    />
                )}
            </Box>
        </Box>
    );

}
